import { Link } from 'react-router-dom'
import { usePageMeta } from '../lib/usePageMeta.js'
import { SITE } from '../lib/site.js'
import {
  CASE_STUDIES,
  PROCESS,
  SERVICES,
  STATS,
  TESTIMONIALS,
  TRADES,
} from '../lib/content.js'
import { Icon } from '../components/icons.jsx'
import {
  Button,
  Container,
  CtaBand,
  Eyebrow,
  IconTile,
  Section,
  SectionHead,
} from '../components/ui.jsx'

// Weekly call volume for the hero's dashboard card (sample numbers).
const HERO_BARS = [34, 48, 42, 60, 72, 66, 88, 96]
const HERO_LEADS = [
  { name: 'Patricia M.', job: 'Roof replacement', city: 'Plano', status: 'Booked' },
  { name: 'Devin R.', job: 'Storm damage inspection', city: 'Frisco', status: 'Quoted' },
  { name: 'Angela T.', job: 'Gutter install', city: 'Allen', status: 'New' },
]
const STATUS_STYLES = {
  Booked: 'bg-[#ecfdf5] text-[#047857]',
  Quoted: 'bg-primary-tint text-primary-dark',
  New: 'bg-[#eff6ff] text-[#1d4ed8]',
}

function Hero() {
  return (
    <section className="relative overflow-hidden bg-white">
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(70%_60%_at_85%_20%,rgba(242,107,29,0.14),transparent_70%)]"
        aria-hidden="true"
      />
      <Container className="relative">
        <div className="grid items-center gap-y-12 py-16 sm:py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-x-16 lg:py-28">
          <div className="flex flex-col items-start gap-y-6">
            <Eyebrow>Marketing for contractors</Eyebrow>
            <h1 className="text-[40px] font-extrabold leading-[1.04] tracking-[-0.03em] text-ink sm:text-[54px] lg:text-[62px]">
              More booked jobs.
              <br />
              <span className="text-primary">Less chasing leads.</span>
            </h1>
            <p className="max-w-[560px] text-[18px] leading-[1.6] text-dim">
              Websites, local SEO, Google Ads and review systems built for roofers, HVAC,
              plumbers, electricians and remodelers. We are judged on one number: jobs on
              your calendar.
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Button to="/contact" size="lg" icon="arrowRight">
                Get a free 48-hour audit
              </Button>
              <Button to="/services" variant="ghost" size="lg">
                See what we do
              </Button>
            </div>
            <ul className="flex flex-wrap items-center gap-x-6 gap-y-2 text-[14px] font-semibold text-dim">
              {['No long-term contracts', 'Live in 30 days', 'You own everything'].map((item) => (
                <li key={item} className="inline-flex items-center gap-x-2">
                  <Icon name="check" size={16} className="text-primary" strokeWidth={3} />
                  {item}
                </li>
              ))}
            </ul>
          </div>

          <div className="relative mx-auto w-full max-w-[520px] lg:max-w-none">
            <div
              className="absolute -inset-4 rounded-[28px] bg-[radial-gradient(60%_60%_at_70%_30%,rgba(242,107,29,0.22),transparent_70%)]"
              aria-hidden="true"
            />
            <div className="relative rounded-[20px] border border-line bg-white p-5 shadow-lift sm:p-6">
              <div className="flex items-start justify-between gap-x-4">
                <div className="flex flex-col gap-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-faint">
                    This month
                  </span>
                  <span className="text-[15px] font-semibold text-ink">
                    Summit Roofing, Dallas
                  </span>
                </div>
                <span className="rounded-full bg-[#ecfdf5] px-[10px] py-[4px] text-[12px] font-bold text-[#047857] whitespace-nowrap">
                  +42% calls
                </span>
              </div>

              <div className="mt-5 grid grid-cols-3 gap-3">
                {[
                  { value: '128', label: 'Calls' },
                  { value: '36', label: 'Quote requests' },
                  { value: '#2', label: 'Map pack rank' },
                ].map((s) => (
                  <div key={s.label} className="rounded-[12px] bg-surface px-3 py-3">
                    <div className="text-[22px] font-extrabold tracking-[-0.02em] text-ink">
                      {s.value}
                    </div>
                    <div className="text-[12px] font-semibold text-dim">{s.label}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 rounded-[12px] border border-line p-4">
                <div className="flex items-center justify-between text-[12px] font-semibold text-dim">
                  <span>Calls per week</span>
                  <span className="text-primary-dark">Last 8 weeks</span>
                </div>
                <div className="mt-3 flex h-[88px] items-end gap-x-2">
                  {HERO_BARS.map((h, i) => (
                    <div
                      key={i}
                      className={`flex-1 rounded-t-[4px] ${i === HERO_BARS.length - 1 ? 'bg-primary' : 'bg-[#f7c9ad]'}`}
                      style={{ height: `${h}%` }}
                    />
                  ))}
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-y-2">
                {HERO_LEADS.map((lead) => (
                  <div
                    key={lead.name}
                    className="flex items-center justify-between gap-x-3 rounded-[10px] bg-surface px-3 py-[10px]"
                  >
                    <div className="flex min-w-0 flex-col">
                      <span className="truncate text-[13px] font-semibold text-ink">
                        {lead.name} · {lead.job}
                      </span>
                      <span className="text-[12px] text-dim">{lead.city}</span>
                    </div>
                    <span
                      className={`rounded-full px-[9px] py-[3px] text-[11px] font-bold whitespace-nowrap ${STATUS_STYLES[lead.status]}`}
                    >
                      {lead.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

function TradesStrip() {
  return (
    <section className="border-y border-line bg-surface py-8">
      <Container>
        <div className="flex flex-col items-center gap-y-5">
          <span className="text-[12px] font-bold uppercase tracking-[0.14em] text-faint">
            Built for the trades
          </span>
          <ul className="flex flex-wrap items-center justify-center gap-x-3 gap-y-3">
            {TRADES.map((t) => (
              <li
                key={t.name}
                className="inline-flex items-center gap-x-2 rounded-full border border-line bg-white px-[14px] py-[8px] text-[13px] font-semibold text-dim"
              >
                <Icon name={t.icon} size={16} className="text-primary" />
                {t.name}
              </li>
            ))}
          </ul>
        </div>
      </Container>
    </section>
  )
}

function ServicesGrid() {
  return (
    <Section id="services">
      <SectionHead
        eyebrow="What we do"
        title="Everything between a homeowner searching and a job on your calendar."
        body="Pick one service or the whole system. Every piece is built for how contractors actually get hired."
      />
      <div className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s) => (
          <Link
            key={s.slug}
            to={`/services#${s.slug}`}
            className="group flex flex-col gap-y-4 rounded-[16px] border border-line bg-white p-7 shadow-card transition-all hover:-translate-y-[2px] hover:border-[#f7c9ad] hover:shadow-lift"
          >
            <IconTile name={s.icon} />
            <h3 className="text-[19px] font-bold leading-[1.25] tracking-[-0.01em] text-ink">
              {s.title}
            </h3>
            <p className="text-[15px] leading-[1.6] text-dim">{s.short}</p>
            <span className="mt-auto inline-flex items-center gap-x-1.5 pt-2 text-[14px] font-semibold text-primary-dark">
              Learn more
              <Icon
                name="arrowRight"
                size={15}
                className="transition-transform group-hover:translate-x-[3px]"
              />
            </span>
          </Link>
        ))}
      </div>
    </Section>
  )
}

function Process() {
  return (
    <Section id="process" tone="surface">
      <SectionHead
        eyebrow="How it works"
        title="Audit, plan, launch, scale."
        body="Four steps, one point of contact, and a number to hold us to at every stage."
      />
      <ol className="mt-14 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {PROCESS.map((p) => (
          <li
            key={p.step}
            className="flex flex-col gap-y-4 rounded-[16px] border border-line bg-white p-7 shadow-card"
          >
            <div className="flex items-center justify-between">
              <IconTile name={p.icon} />
              <span className="text-[13px] font-bold tracking-[0.1em] text-faint">{p.step}</span>
            </div>
            <div className="flex flex-col gap-y-1">
              <h3 className="text-[20px] font-bold tracking-[-0.01em] text-ink">{p.title}</h3>
              <p className="text-[14px] font-semibold text-primary-dark">{p.lead}</p>
            </div>
            <p className="text-[15px] leading-[1.6] text-dim">{p.body}</p>
          </li>
        ))}
      </ol>
    </Section>
  )
}

function StatBand() {
  return (
    <section className="bg-night py-16 text-white sm:py-20">
      <Container>
        <div className="grid gap-y-10 sm:grid-cols-2 lg:grid-cols-4 lg:gap-x-8">
          {STATS.map((s) => (
            <div
              key={s.label}
              className="flex flex-col gap-y-2 border-l-2 border-primary pl-5"
            >
              <span className="text-[44px] font-extrabold leading-none tracking-[-0.03em] text-white">
                {s.value}
              </span>
              <span className="text-[14px] font-semibold text-[#b6bfd0]">{s.label}</span>
            </div>
          ))}
        </div>
      </Container>
    </section>
  )
}

function ResultsPreview() {
  return (
    <Section id="results">
      <div className="flex flex-col gap-y-8 lg:flex-row lg:items-end lg:justify-between">
        <SectionHead
          align="left"
          eyebrow="Results"
          title="Real trades, real markets, real numbers."
          body="A few of the companies we run marketing for, and what changed."
        />
        <Button to="/results" variant="ghost" icon="arrowRight" className="shrink-0">
          All case studies
        </Button>
      </div>
      <div className="mt-12 grid gap-6 lg:grid-cols-3">
        {CASE_STUDIES.map((c) => (
          <article
            key={c.company}
            className="flex flex-col gap-y-5 rounded-[16px] border border-line bg-white p-7 shadow-card"
          >
            <div className="flex items-center justify-between gap-x-3">
              <span className="rounded-full bg-primary-tint px-[10px] py-[4px] text-[12px] font-bold uppercase tracking-[0.08em] text-primary-dark">
                {c.trade}
              </span>
              <span className="text-[13px] text-faint">{c.market}</span>
            </div>
            <h3 className="text-[24px] font-extrabold leading-[1.15] tracking-[-0.02em] text-ink">
              {c.headline}
            </h3>
            <p className="text-[15px] leading-[1.6] text-dim">{c.did}</p>
            <dl className="mt-auto grid grid-cols-3 gap-x-3 border-t border-line pt-5">
              {c.results.map((r) => (
                <div key={r.label} className="flex flex-col gap-y-1">
                  <dt className="order-2 text-[12px] leading-[1.3] text-dim">{r.label}</dt>
                  <dd className="order-1 text-[20px] font-extrabold tracking-[-0.02em] text-ink">
                    {r.value}
                  </dd>
                </div>
              ))}
            </dl>
          </article>
        ))}
      </div>
    </Section>
  )
}

function Testimonials() {
  return (
    <Section id="testimonials" tone="surface">
      <SectionHead
        eyebrow="From the owners"
        title="What contractors say after the first 90 days."
      />
      <div className="mt-12 grid gap-6 md:grid-cols-3">
        {TESTIMONIALS.map((t) => (
          <figure
            key={t.name}
            className="flex flex-col gap-y-5 rounded-[16px] border border-line bg-white p-7 shadow-card"
          >
            <Icon name="quote" size={26} className="text-primary" strokeWidth={1.6} />
            <blockquote className="text-[17px] leading-[1.55] text-ink">
              “{t.quote}”
            </blockquote>
            <figcaption className="mt-auto flex flex-col gap-y-[2px] border-t border-line pt-5">
              <span className="text-[14px] font-bold text-ink">{t.name}</span>
              <span className="text-[13px] text-dim">{t.role}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  )
}

export default function Home() {
  usePageMeta('', SITE.description)
  return (
    <>
      <Hero />
      <TradesStrip />
      <ServicesGrid />
      <Process />
      <StatBand />
      <ResultsPreview />
      <Testimonials />
      <CtaBand phone={{ href: SITE.phoneHref, label: SITE.phone }} />
    </>
  )
}
